import { FormField } from "@/ui/form";
import { cn } from "@gettactic/helpers";
import { Switch } from "@cmp-src/primitives/Switch";
import { Label } from "@cmp-src/primitives/label";
import { Video, VideoOff } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import {
  OnlineMeetingIntegrations,
  OnlineMeetingProvider,
  OrganizationSettings
} from "@gettactic/api";
import {
  MS_1_MINUTE,
  useAuthenticated,
  useOrganizationSettings
} from "@gettactic/hooks";

export interface ReserveFormOnlineMeetingProps {
  isMeetingRoom: boolean;
  form: UseFormReturn<any>;
  className?: string;
  hasMSCalendar: boolean | undefined;
  hasGoogleCalendar: boolean | undefined;
  isEdit: boolean;
  isMinimalistic?: boolean;
}

const meetingProviders: Record<
  OnlineMeetingProvider,
  {
    keyInSettings: OnlineMeetingIntegrations;
    value: OnlineMeetingProvider;
    label: string;
    icon: string;
  }
> = {
  teams_for_business: {
    keyInSettings: OnlineMeetingIntegrations.MICROSOFT_ONLINE_MEETING_PROVIDERS,
    value: "teams_for_business",
    label: "Microsoft Teams",
    icon: "🟣"
  },
  hangouts_meet: {
    keyInSettings: OnlineMeetingIntegrations.GOOGLE_ONLINE_MEETING_PROVIDERS,
    value: "hangouts_meet",
    label: "Google Meet",
    icon: "🟢"
  }
};

export const ReserveFieldOnlineMeeting: React.FC<
  ReserveFormOnlineMeetingProps
> = ({
  className,
  hasMSCalendar,
  hasGoogleCalendar,
  isMeetingRoom,
  form,
  isEdit,
  isMinimalistic
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = authenticatedUser?.organization?.id;

  // Watch form values
  const onlineMeeting = form.watch("online_meeting");
  const selectedProvider = onlineMeeting?.provider;
  const onlineMeetingEnabled = !!selectedProvider;
  // const customMeetingLink = form.watch('customMeetingLink');
  const noCalendar = !hasMSCalendar && !hasGoogleCalendar;
  if (!isMeetingRoom || noCalendar) return null;

  const { data: organizationSettings } = useOrganizationSettings({
    id: organizationId,
    refetchInterval: MS_1_MINUTE,

    // has to have a calendar enabled in the organization settings
    disabled: !organizationId || !(hasMSCalendar || hasGoogleCalendar)
  });

  const integrationType = hasMSCalendar
    ? OnlineMeetingIntegrations.MICROSOFT_ONLINE_MEETING_PROVIDERS
    : OnlineMeetingIntegrations.GOOGLE_ONLINE_MEETING_PROVIDERS;
  const providers =
    (organizationSettings as OrganizationSettings)?.calendar_settings?.[
      integrationType
    ] || [];

  // Determine which provider to use based on available calendars
  const availableProvider: OnlineMeetingProvider | null = hasMSCalendar
    ? "teams_for_business"
    : hasGoogleCalendar
      ? "hangouts_meet"
      : null;

  // if settings for this integration are empty, don't show the field,
  // that means that the integration is not enabled in respectful integration settings
  if (!providers.length) {
    return null;
  }

  if (isMinimalistic) {
    return (
      <div className={cn("flex items-center justify-between gap-3", className)}>
        <Label htmlFor="online-meeting" className="font-inter font-medium text-neutral-800 text-sm">
          Add video conferencing
        </Label>
        <FormField
          control={form.control}
          name="online_meeting.provider"
          render={({ field }) => (
            <Switch
              id="online-meeting"
              size="sm"
              checked={onlineMeetingEnabled}
              disabled={field.disabled}
              onCheckedChange={(checked) => {
                if (checked && availableProvider) {
                  form.setValue("online_meeting", {
                    provider: availableProvider
                  });
                } else {
                  form.setValue("online_meeting", null);
                }
              }}
            />
          )}
          disabled={
            onlineMeeting?.status === "success" ||
            (isEdit && onlineMeeting?.provider === "teams_for_business")
          }
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onlineMeetingEnabled ? (
            <Video className="h-5 w-5 text-blue-600" />
          ) : (
            <VideoOff className="h-5 w-5 text-muted-foreground" />
          )}
          <div>
            <Label htmlFor="online-meeting" className="font-medium text-base">
              Online Meeting
            </Label>
            <p className="text-muted-foreground text-sm">
              Add a video conference link to this meeting
            </p>
          </div>
        </div>
        <FormField
          control={form.control}
          name="online_meeting.provider"
          render={({ field }) => (
            <Switch
              id="online-meeting"
              checked={onlineMeetingEnabled}
              disabled={field.disabled}
              onCheckedChange={(checked) => {
                if (checked && availableProvider) {
                  form.setValue("online_meeting", {
                    provider: availableProvider
                  });
                } else {
                  form.setValue("online_meeting", null);
                }
              }}
            />
          )}
          disabled={
            onlineMeeting?.status === "success" ||
            (isEdit && onlineMeeting?.provider === "teams_for_business")
          }
        />
      </div>

      {/* TODO: We'll need this part when we'll have multiple providers */}
      {/* For now each calendar has only one provider */}
      {/* {onlineMeetingEnabled && provider && (
        <div className="space-y-4 border-neutral-200 border-t pt-4">
          <div className="space-y-2">
            <Label>Meeting Provider</Label>
            <FormField
              control={form.control}
              name="onlineMeetingProvider"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {meetingProviders.map((provider) => (
                          <SelectItem
                            key={provider.value}
                            value={provider.value}
                          >
                            <div className="flex items-center gap-2">
                              <span>{provider.icon}</span>
                              {provider.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {selectedProvider === 'custom' && (
            <div className="space-y-2">
              <Label>Custom Meeting Link</Label>
              <FormField
                control={form.control}
                name="customMeetingLink"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="https://your-meeting-link.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {selectedProvider && selectedProvider !== 'custom' && (
            <div className="rounded-md bg-blue-50 p-3">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> A{' '}
                {
                  meetingProviders.find((p) => p.value === selectedProvider)
                    ?.label
                }{' '}
                meeting link will be automatically generated when you create
                this reservation.
              </p>
            </div>
          )}
        </div>
      )} */}
    </div>
  );
};
